import './styles/design-system.css';
import 'primeicons/primeicons.css';
import '@agro1desenvolvimento/react-components';
import 'primereact/resources/themes/bootstrap4-light-blue/theme.css';
import './styles/styles.scss';
import './configuration';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { useEffect, useState } from 'react';
import AppMenuBar from './components/AppMenuBar/AppMenuBar';
import Agro1Toast from './components/Agro1Toast';
import Agro1Loader from './components/Agro1Loader';
import {
  LinhaTempo,
  PropriedadeSelectPage,
  ErrorPage,
  RecomendacoesSemeaduraPage,
  RecomendacoesSemeaduraCreateEditPage,
  TratamentoSementesPage,
  TratamentoSementesCreateEditPage,
  LeftMenu,
  GerenciadorDados,
  AtividadePage,
  AtividadeCreateEditPage,
  PluviometroPage,
  PluviometroCreateEditPage,
  OperacaoPage,
  OperacaoCreateEditPage,
  LoginPage,
  RelatorioVisitaListPage,
  RelatorioVisitaCreatePage,
  RelatorioVisitaViewPage,
} from './features';
import {
  AuthSession,
  LocalStorageService,
  ParametroService,
  PermissoesService,
  UsuariosService,
} from './services';
import usePermissoesStore from './services/permissoes-store';
import { AUTH_SESSION_CHANGED_EVENT } from './services/auth-session';
import { ParametrosProps, Permissoes } from './@types';

const Layout = () => (
  <>
    <Agro1Toast />
    <Agro1Loader />
    <AppMenuBar />
    <div className="page-container">
      <Outlet />
    </div>
  </>
);

const SideBar = () => (
  <div className="sidebar-page-container">
    <LeftMenu />
    <Outlet />
  </div>
);

const PublicLayout = () => (
  <>
    <Agro1Toast />
    <Agro1Loader />
    <Outlet />
  </>
);

const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();

  if (!AuthSession.isAuthenticated()) {
    const redirectPath = AuthSession.normalizeRedirect(
      `${location.pathname}${location.search}`,
    );
    return <Navigate to={AuthSession.getLoginUrl(redirectPath)} replace />;
  }

  return children;
};

const getRoutesWithPermissions = ({
  permissoes,
  parametro,
}: {
  permissoes: Permissoes;
  parametro?: ParametrosProps;
}) => [
  // Rota desabilitada por enquanto, iremos deixar somente a da v1
  {
    path: '/',
    element: <PropriedadeSelectPage />,
    hasAccess: false,
  },
  {
    path: '/index.html',
    element: <PropriedadeSelectPage />,
    hasAccess: false,
  },
  {
    path: '/recomendacoes-semeadura',
    hasAccess:
      permissoes.recomendacaoSemeadura.read ||
      permissoes.recomendacaoSemeadura.manage ||
      permissoes.recomendacaoSemeadura.editDelete,
    children: [
      {
        path: '',
        element: <RecomendacoesSemeaduraPage />,
        hasAccess:
          permissoes.recomendacaoSemeadura.read ||
          permissoes.recomendacaoSemeadura.manage ||
          permissoes.recomendacaoSemeadura.editDelete,
      },
      {
        path: 'create',
        element: <RecomendacoesSemeaduraCreateEditPage />,
        hasAccess: permissoes.recomendacaoSemeadura.manage,
      },
      {
        path: 'edit/:id',
        element: <RecomendacoesSemeaduraCreateEditPage />,
        hasAccess:
          permissoes.recomendacaoSemeadura.manage ||
          permissoes.recomendacaoSemeadura.editDelete,
      },
    ],
  },
  {
    path: 'tratamento-sementes',
    hasAccess:
      permissoes.tratamentoSemente.read || permissoes.tratamentoSemente.manage,
    children: [
      {
        path: '',
        element: <TratamentoSementesPage />,
        hasAccess:
          permissoes.tratamentoSemente.read ||
          permissoes.tratamentoSemente.manage,
      },
      {
        path: 'create',
        element: <TratamentoSementesCreateEditPage />,
        hasAccess: permissoes.tratamentoSemente.manage,
      },
      {
        path: 'edit/:id',
        element: <TratamentoSementesCreateEditPage />,
        hasAccess: permissoes.tratamentoSemente.manage,
      },
    ],
  },
  {
    path: '/linha-tempo',
    element: <LinhaTempo />,
    hasAccess: permissoes.timeline.read,
  },
  {
    path: '/relatorio-visita',
    hasAccess: permissoes.visitsReport.read,
    children: [
      {
        path: '',
        element: <RelatorioVisitaListPage />,
        hasAccess: permissoes.visitsReport.read,
      },
      {
        path: 'novo',
        element: <RelatorioVisitaCreatePage />,
        hasAccess: permissoes.visitsReport.read,
      },
    ],
  },
  // Rota desabilitada por enquanto, iremos deixar somente a da v1
  {
    path: '/gerenciador-dados',
    element: <SideBar />,
    hasAccess: false,
    children: [
      {
        path: '',
        element: <GerenciadorDados />,
        hasAccess: true,
      },
      {
        path: 'atividades',
        hasAccess: permissoes.atividade.read || permissoes.atividade.manage,
        children: [
          {
            path: '',
            element: <AtividadePage />,
            hasAccess:
              (permissoes.atividade.read || permissoes.atividade.manage) &&
              parametro?.naoClienteAgrogestao,
          },
          {
            path: 'create',
            element: <AtividadeCreateEditPage />,
            hasAccess:
              permissoes.atividade.manage && parametro?.naoClienteAgrogestao,
          },
          {
            path: 'edit/:id',
            element: <AtividadeCreateEditPage />,
            hasAccess:
              permissoes.atividade.manage && parametro?.naoClienteAgrogestao,
          },
        ],
      },
      {
        path: 'pluviometros',
        hasAccess: permissoes.pluviometro.read || permissoes.pluviometro.manage,
        children: [
          {
            path: '',
            element: <PluviometroPage />,
            hasAccess:
              (permissoes.pluviometro.read || permissoes.pluviometro.manage) &&
              parametro?.naoClienteAgrogestao,
          },
          {
            path: 'create',
            element: <PluviometroCreateEditPage />,
            hasAccess:
              permissoes.pluviometro.manage && parametro?.naoClienteAgrogestao,
          },
          {
            path: 'edit/:id',
            element: <PluviometroCreateEditPage />,
            hasAccess:
              permissoes.pluviometro.manage && parametro?.naoClienteAgrogestao,
          },
        ],
      },
      {
        path: 'operacoes',
        hasAccess: permissoes.operacao.read || permissoes.operacao.manage,
        children: [
          {
            path: '',
            element: <OperacaoPage />,
            hasAccess:
              (permissoes.operacao.read || permissoes.operacao.manage) &&
              parametro?.naoClienteAgrogestao,
          },
          {
            path: 'create',
            element: <OperacaoCreateEditPage />,
            hasAccess:
              permissoes.operacao.manage && parametro?.naoClienteAgrogestao,
          },
          {
            path: 'edit/:id',
            element: <OperacaoCreateEditPage />,
            hasAccess:
              permissoes.operacao.manage && parametro?.naoClienteAgrogestao,
          },
        ],
      },
    ],
  },
];

const App = () => {
  const [parametro, setParametro] = useState<ParametrosProps>();
  const [, setAuthStateVersion] = useState(0);

  const routesWithPermission = getRoutesWithPermissions({
    permissoes: PermissoesService,
    parametro,
  });

  const permittedRoutes = routesWithPermission
    .filter(({ hasAccess }) => hasAccess)
    .map((route) => {
      if (!route.children?.length) return route;

      const permittedchildrens = route.children.filter(
        (children) => children.hasAccess,
      );

      return {
        ...route,
        children: permittedchildrens,
      };
    });

  const routes = createBrowserRouter(
    [
      {
        element: <PublicLayout />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: '/login',
            element: <LoginPage />,
          },
          {
            path: '404',
            element: <ErrorPage isNotFound />,
          },
          {
            path: '/relatorio-visita-aqila/:uuid',
            element: <RelatorioVisitaViewPage />,
          },
        ],
      },
      {
        element: (
          <RequireAuth>
            <Layout />
          </RequireAuth>
        ),
        children: [
          {
            errorElement: <ErrorPage />,
            children: [
              ...permittedRoutes,
              {
                path: '*',
                loader: () => {
                  throw new Response('Página não encontrada', { status: 404 });
                },
              },
            ],
          },
        ],
      },
    ],
    { basename: '/aqila-2' },
  );

  useEffect(() => {
    const loadParametroIfAuthenticated = async () => {
      if (!AuthSession.isAuthenticated()) {
        setParametro(undefined);
        return;
      }

      try {
        setParametro(await ParametroService.parametro());
      } catch (error) {
        Agro1Toast.show({
          detail: 'Falha ao buscar parâmetros.',
          severity: 'error',
          sticky: true,
        });

        console.error(error);
      }
    };

    const refreshPermissions = async () => {
      if (!AuthSession.isAuthenticated()) return;

      try {
        const userId = LocalStorageService.getItem('user')?.id;
        if (!userId) return;

        const user = await UsuariosService.getById(userId);
        const newPermissions = user.funcao?.permissions ?? [];
        const currentPermissions = usePermissoesStore.getState().permissions;

        const changed =
          JSON.stringify(newPermissions) !== JSON.stringify(currentPermissions);

        if (changed) {
          usePermissoesStore.getState().setPermissions(newPermissions);
          setAuthStateVersion((current) => current + 1);
        }
      } catch (error) {
        console.error('Falha ao atualizar permissões:', error);
      }
    };

    const onAuthSessionChanged = () => {
      const permissions =
        LocalStorageService.getItem('user')?.funcao?.permissions ?? [];
      usePermissoesStore.getState().setPermissions(permissions);
      setAuthStateVersion((current) => current + 1);
      loadParametroIfAuthenticated();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshPermissions();
      }
    };

    loadParametroIfAuthenticated();
    refreshPermissions();

    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, onAuthSessionChanged);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener(
        AUTH_SESSION_CHANGED_EVENT,
        onAuthSessionChanged,
      );
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return <RouterProvider router={routes} />;
};

export default App;
